import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/**
 * StyleHub — homepage section linking to the five per-style pillar
 * landing pages (Kundan, Jadau, Polki, Diamond, Temple).
 *
 * SEO purpose: concentrates PageRank on the exact-match commercial
 * landing pages and gives Google a strong internal-link signal for
 * which URLs to rank for each style-specific query.
 *
 * UX purpose: lets a jeweller land on the homepage, immediately
 * self-select their specialisation, and dive into a workflow tuned
 * to it.
 */
const STYLES = [
  {
    slug: "ai-kundan-jewellery-design",
    label: "Kundan",
    caption: "Scalloped 24kt edges, foil-backed Polki, meenakari reverse",
  },
  {
    slug: "ai-jadau-jewellery-design",
    label: "Jadau",
    caption: "Mughal-era craft, jaali shoulders, hand-embedded stones",
  },
  {
    slug: "ai-polki-jewellery-design",
    label: "Polki Diamond",
    caption: "Uncut, foil-backed, diffuse glow — not brilliant sparkle",
  },
  {
    slug: "ai-diamond-jewellery-design",
    label: "Diamond",
    caption: "Solitaire, halo, cluster, tennis, cocktail — modern-cut",
  },
  {
    slug: "ai-temple-jewellery-design",
    label: "Temple",
    caption: "Deity iconography, nakashi relief, granulation borders",
  },
];

// PhotoRoom + Catalog + Students — grouped as service pillars, distinct
// from the style pillars above. Shown as a second row so a jeweller can
// self-route by both style (Kundan / Jadau / …) AND service (touch-up /
// model shoot / creative shoot / …).
const SERVICES = [
  {
    slug: "ai-render-from-cad-jewellery",
    label: "Render from CAD",
    caption: "Rhino / MatrixGold / JewelCAD screenshot → photoreal render in 40 seconds",
    kind: "Studio",
  },
  {
    slug: "ai-component-detection-auto-split-jewellery",
    label: "Auto-Split",
    caption: "Detect every chaton, prong, jaali, meena — split into addressable components",
    kind: "Studio",
  },
  {
    slug: "ai-2d-image-to-3d-stl-jewellery",
    label: "2D → 3D STL",
    caption: "Any image → production-ready STL / GLB / OBJ / FBX for casting or 3D printing",
    kind: "Studio",
  },
  {
    slug: "ai-temple-idol-sculpting-3d-cad",
    label: "Temple Idol 3D",
    caption: "Sculpt Lakshmi / Ganesh / Krishna, split components, generate production 3D",
    kind: "Studio",
  },
  {
    slug: "ai-flora-fauna-motif-3d-jewellery",
    label: "Flora & Fauna 3D",
    caption: "Peacock, lotus, snake, butterfly — motif-split into components with 3D per part",
    kind: "Studio",
  },
  {
    slug: "ai-photo-touch-up-diamond-jewellery",
    label: "Diamond Touch-Up",
    caption: "Retouch diamond DSLR shots — sharpen facets, correct dispersion, matte-black backdrop",
    kind: "PhotoRoom",
  },
  {
    slug: "ai-photo-touch-up-gold-jewellery",
    label: "Gold Touch-Up",
    caption: "Karat-accurate warmth (22kt / 18kt / rose), filigree preserved, 4K & 8K exports",
    kind: "PhotoRoom",
  },
  {
    slug: "ai-photo-touch-up-kundan-jewellery",
    label: "Kundan Touch-Up",
    caption: "Preserve Polki glow, sharpen scalloped 24kt kundan edges, meenakari intact",
    kind: "PhotoRoom",
  },
  {
    slug: "ai-photo-touch-up-jadau-jewellery",
    label: "Jadau Touch-Up",
    caption: "Hand-worked matte gold preserved, jaali openwork sharpened, heritage backdrops",
    kind: "PhotoRoom",
  },
  {
    slug: "ai-model-photoshoot-for-jewellery",
    label: "Model Photoshoot",
    caption: "Put your piece on a bridal / editorial / contemporary AI model — no studio, no MUA",
    kind: "Catalog",
  },
  {
    slug: "ai-creative-photoshoot-for-jewellery",
    label: "Creative Photoshoot",
    caption: "Editorial scenes — palace, marble, silk drape, petals — brand-defining imagery",
    kind: "Catalog",
  },
  {
    slug: "ai-custom-sizing-for-jewellery-photography",
    label: "Custom Sizing",
    caption: "One upload → Instagram, Reels, e-commerce, catalogue A4 — sharp at every size",
    kind: "Catalog",
  },
];

export default function StyleHub() {
  return (
    <section
      id="styles"
      data-testid="landing-style-hub"
      className="relative py-24 sm:py-28 border-t border-white/5"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-4">
          Design by style
        </div>
        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-3xl">
          One studio, fluent in every Indian jewellery style.
        </h2>
        <p className="mt-5 text-white/60 text-sm md:text-base max-w-2xl leading-relaxed">
          Facetra ships with dedicated prompt libraries for the major styles — pick your
          specialisation to see a workflow, sample prompts and pricing tuned to it.
        </p>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {STYLES.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`style-hub-link-${s.slug}`}
              className="group relative rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
            >
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mb-1.5">
                For {s.label} ateliers
              </div>
              <h3 className="font-display text-base text-white group-hover:text-[#D4AF37] transition leading-snug">
                AI Tool for {s.label} Jewellery Design
              </h3>
              <p className="mt-2 text-[11.5px] text-white/55 leading-relaxed">
                {s.caption}
              </p>
              <div className="mt-2.5 text-[10px] text-white/40 inline-flex items-center gap-1">
                Explore workflow <ArrowRight className="w-2.5 h-2.5" />
              </div>
            </Link>
          ))}
        </div>

        {/* Services row — PhotoRoom + Catalog pillars */}
        <div className="mt-16 mb-4 text-[10px] uppercase tracking-[0.28em] text-[#D4AF37]">
          Studio, PhotoRoom &amp; Catalog services
        </div>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.1] max-w-3xl">
          One studio, every deliverable — sketch to STL, touch-up to editorial.
        </h2>
        <p className="mt-4 text-white/60 text-sm md:text-base max-w-2xl leading-relaxed">
          Render from CAD, component split, 2D-to-3D STL, temple idol sculpting, motif
          3D, product retouching, model shoots, editorial creative and custom sizing —
          the full atelier pipeline.
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/${s.slug}`}
              data-testid={`service-hub-link-${s.slug}`}
              className="group relative rounded-xl border border-white/8 bg-[#0E0E0E] p-4 hover:border-[#D4AF37]/30 transition"
            >
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#D4AF37]/80 mb-1.5">
                {s.kind}
              </div>
              <h3 className="font-display text-base text-white group-hover:text-[#D4AF37] transition leading-snug">
                {s.label}
              </h3>
              <p className="mt-2 text-[11.5px] text-white/55 leading-relaxed">
                {s.caption}
              </p>
              <div className="mt-2.5 text-[10px] text-white/40 inline-flex items-center gap-1">
                See workflow <ArrowRight className="w-2.5 h-2.5" />
              </div>
            </Link>
          ))}
        </div>

        {/* Students callout */}
        <div className="mt-16 rounded-2xl border border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-7 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] mb-2">
              For students &amp; designers
            </div>
            <h3 className="font-display text-xl text-white leading-tight">
              Build a jewellery design portfolio that gets you hired.
            </h3>
            <p className="mt-2 text-white/60 text-sm max-w-xl">
              NIFT, IIAD, GIA, JD Institute and Pearl Academy students get 10 free credits
              on signup and the same Studio, PhotoRoom and Catalog features as working
              ateliers.
            </p>
          </div>
          <Link
            to="/for-students"
            data-testid="landing-students-cta"
            className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#D4AF37] px-5 py-2.5 text-sm font-medium text-black hover:bg-[#F3CE5A] transition"
          >
            For students <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}
